import React, { FC, useMemo } from "react"
import {
    CopyButton,
    Loader,
    Title,
    ClickAwayListener,
    Tooltip
} from "dash-styled"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import {
    LabelerType,
    TaskModel,
    TaskStatus
} from "../../../../../../../Models/API/TaskModel"
import { LabelerUsernameWithStatus } from "./LabelerUsernameWithStatus"
import { useHero } from "../../../../../../../Hooks/useHero"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { useAssumedRoles } from "../../../../../../../Hooks/useAssumedRoles"
import { hasAppPermission } from "../../../../../../../Models/Client/ProjectRole"
import { CollaboratorStatus } from "../../../../../../../Models/API/CollaboratorModel"
import { useAppWorker } from "../../../../../../../Hooks/useAppWorker"

interface TaskAssigneeViewProps {
    appID: string
    model: TaskModel
    className?: string
    tooltipPlacement?: "bottom" | "right"
}

export const TaskAssigneeView: FC<TaskAssigneeViewProps> = ({
    appID,
    model,
    className,
    tooltipPlacement = "right"
}) => {
    const [isLabelerDetailOpen, openLabelerDetail, closeLabelerDetail] =
        useBoolean(false)
    const rolesFetch = useAssumedRoles(appID)
    const canViewDetail = hasAppPermission(
        getValue(rolesFetch) ?? [],
        model.labelerType === LabelerType.hero ? "crowd:view" : "workers:view"
    )
    const skipFetchDetail = !isLabelerDetailOpen || !canViewDetail
    const heroFetch = useHero(model.labelerId, skipFetchDetail)
    const workerFetch = useAppWorker(
        appID,
        model.labelerUsername,
        skipFetchDetail
    )
    const userOrHero = useMemo<
        { email: string; name?: string } | undefined
    >(() => {
        if (model.labelerType === LabelerType.collaborator) {
            return hasValue(workerFetch) &&
                workerFetch.value.worker.type === "USER"
                ? {
                      email: workerFetch.value.worker.userEmail,
                      name: workerFetch.value.worker.userUsername
                  }
                : undefined
        }

        if (!hasValue(heroFetch)) {
            return undefined
        }

        const { email, firstname, lastname } = heroFetch.value

        return {
            email,
            name:
                firstname === null
                    ? lastname === null
                        ? undefined
                        : lastname
                    : lastname === null
                    ? firstname
                    : [firstname, lastname].join(" ")
        }
    }, [heroFetch, model.labelerType, workerFetch])

    if (model.status === TaskStatus.SCHEDULED) {
        return null
    }

    return (
        <ClickAwayListener onClickAway={closeLabelerDetail}>
            <Tooltip
                interactive={true}
                placement={tooltipPlacement}
                onClose={closeLabelerDetail}
                open={isLabelerDetailOpen}
                title={
                    <div
                        className="font-averta"
                        onClick={e => {
                            // When used in table row, need to stop event propagation to prevent unwanted navigation
                            e.stopPropagation()
                        }}
                    >
                        {userOrHero === undefined ? (
                            <Loader size="xxxl" />
                        ) : (
                            <>
                                <Title
                                    component="h3"
                                    variant="heading3"
                                    className="mb-1"
                                >
                                    {userOrHero.name ?? (
                                        <span className="text-rhino-300">
                                            No name
                                        </span>
                                    )}
                                </Title>
                                <p className="text-smoke-810 mb-3">
                                    {model.labelerType ===
                                    LabelerType.collaborator
                                        ? `Project worker (${
                                              model.labelerStatus ===
                                              CollaboratorStatus.DELETED
                                                  ? "deleted"
                                                  : model.labelerStatus ===
                                                    CollaboratorStatus.INACTIVE
                                                  ? "inactive"
                                                  : "active"
                                          })`
                                        : "super.AI worker"}
                                </p>
                                <p>
                                    Email: {userOrHero.email}
                                    <CopyButton
                                        variant="simple"
                                        value={userOrHero.email}
                                    />{" "}
                                </p>
                            </>
                        )}
                    </div>
                }
                classes={{
                    tooltip:
                        "mt-1 p-3 bg-white text-rhino-900 text-sm rounded shadow border border-smoke-890"
                }}
            >
                <div
                    className={`inline-block ${
                        canViewDetail
                            ? "border-0 border-b border-dashed border-smoke-810 cursor-pointer"
                            : ""
                    } ${className ?? ""}`}
                    onMouseEnter={
                        canViewDetail
                            ? e => {
                                  e.stopPropagation()
                                  openLabelerDetail()
                              }
                            : undefined
                    }
                >
                    {model.labelerType === LabelerType.hero ? (
                        "super.AI worker"
                    ) : model.labelerUsername === undefined ? null : (
                        <LabelerUsernameWithStatus
                            username={model.labelerUsername}
                            status={model.labelerStatus}
                        />
                    )}
                </div>
            </Tooltip>
        </ClickAwayListener>
    )
}
